import React, { useEffect, useState } from 'react'
import ReactHtmlParser from 'react-html-parser'
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemPanel
} from 'react-accessible-accordion'
import Button from '../../atoms/Button/Button'
import BlogCopyBlock from '../ContentPicture/BlogCopyBlock'
import { splitHtmlAtH2 } from '../../../utils/splitHtmlAtH2.js'
import { getParentPageURLSegment } from '../../../domain/blogPosts/getParentPageURLSegment'
import { getReturnToBlogHomeLink } from '../../../domain/blogPosts/getReturnToBlogHomeLink'
import { ArrowLeft } from 'react-bootstrap-icons'
import IconChevronDown from '@/components/IconChevronDown'
import ArticlesGrid from '@/components/organisms/ArticlesGrid/ArticlesGrid'
import { scrollToHref } from '../../../utils/scrollTo'
import { NearbyBlogRelatedCentersCarousel } from './NearbyBlogRelatedCarousel'
import { generateNearbyBlogRelatedCenters } from '@/domain/blogPosts/generateNearbyBlogRelatedCenters'
import { isNonEmptyArray, isNonEmptyString } from '@/utils/checks'
import { getRelatedCenterTermLink } from '@/domain/blogPosts/getNearbyRelatedCenterLink'
import { getReturnToBlogHomeTitle } from '@/domain/blogPosts/getReturnToBlogHomeTitle'
import BlogHero from '@/components/blogHero/BlogHero'

const isAllowedScriptSource = (src) => {
  const allowedDomains = ['buzzsprout.com']
  try {
    const url = new URL(src)
    return allowedDomains.some((domain) => url.hostname.endsWith(domain))
  } catch {
    return false
  }
}

const containsSuspiciousPatterns = (content) => {
  const suspiciousPatterns = ['eval', 'document.write', 'window.location']
  return suspiciousPatterns.some((pattern) => content.includes(pattern))
}

const isAllowedScriptContent = (content) => {
  return content && !containsSuspiciousPatterns(content)
}

const BreadcrumbLink = ({ isNews, isVoices, isCircleOfChairs, isPodcast }) => {
  const title = getReturnToBlogHomeLink({
    isNews,
    isVoices,
    isCircleOfChairs,
    isPodcast
  })
  const slug = getParentPageURLSegment(
    isNews,
    isVoices,
    isCircleOfChairs,
    isPodcast
  )

  return (
    <a
      className="rounded-md px-1 py-0.5 text-gray-900 no-underline hover:bg-gray-200 active:bg-gray-300"
      href={`/${slug}/`}
    >
      {title}
    </a>
  )
}

const ReturnToBlogHomeLink = ({
  isNews,
  isVoices,
  isCircleOfChairs,
  isPodcast
}) => {
  const title = getReturnToBlogHomeTitle({
    isNews,
    isVoices,
    isCircleOfChairs,
    isPodcast
  })
  const slug = getParentPageURLSegment(
    isNews,
    isVoices,
    isCircleOfChairs,
    isPodcast
  )

  return (
    <a
      className="flex flex-row items-center justify-start gap-3"
      href={`/${slug}/`}
    >
      <ArrowLeft size="24" />
      <span className="text-lg font-medium text-gray-1100 underline md:no-underline md:hover:underline">
        {title}
      </span>
    </a>
  )
}

const BlogContainerDesktop = ({
  pdfDownloadLink,
  firstTag,
  slicedArticles,
  blogTitle,
  blogSlug,
  publishDate,
  tableOfContents,
  relatedCenterTermData,
  reviewedDate,
  keyPoints,
  citedSources,
  content,
  authorData,
  reviewerData,
  isNews,
  isVoices,
  isCircleOfChairs,
  isPodcast,
  heroOptions
}) => {
  const htmlContentSplits = splitHtmlAtH2(content)

  const [embeddedScriptTagSrc, setEmbeddedScriptTagSrc] = useState(null)

  const downloadAsPdf = () => {
    fetch(`${pdfDownloadLink}`).then((response) => {
      response
        .blob()
        .then((blob) => {
          const fileURL = window.URL.createObjectURL(blob)
          let alink = document.createElement('a')
          alink.href = fileURL
          alink.download = `${blogTitle.replace(/\s+/g, '-').toLowerCase()}.pdf`
          alink.click()
        })
        .catch((e) => {
          console.log(e)
        })
    })
  }

  const [nearbyRelatedCenters, setNearbyRelatedCenters] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const getCarouselCenters = async ({ relatedCenterTermData }) => {
    return await generateNearbyBlogRelatedCenters({
      relatedCenterTermData: relatedCenterTermData
    })
      .then(({ hits }) => {
        return hits
      })
      .catch((error) => {
        console.error('Error generating nearby related centers:', error)
        return []
      })
  }

  useEffect(() => {
    getCarouselCenters({
      relatedCenterTermData: relatedCenterTermData
    })
      .then((hits) => {
        setNearbyRelatedCenters(hits)
        setIsLoading(false)
      })
      .catch((error) => {
        setNearbyRelatedCenters([])
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    const tableOfContents = document.getElementById('table-of-contents')

    if (tableOfContents) {
      const anchors = tableOfContents.querySelectorAll('a[href^="#"]')

      anchors.forEach((anchor) => {
        anchor.addEventListener('click', handleTableOfContentsAnchorClick)
      })

      return () => {
        anchors.forEach((anchor) => {
          anchor.removeEventListener('click', handleTableOfContentsAnchorClick)
        })
      }
    }
  }, [])

  useEffect(() => {
    if (embeddedScriptTagSrc) {
      const headTag = document.getElementsByTagName('head')[0]
      const scriptTag = document.createElement('script')
      scriptTag.src = embeddedScriptTagSrc
      scriptTag.async = true
      headTag.appendChild(scriptTag)
    }
  }, [embeddedScriptTagSrc])

  const nearbyRelatedCenterLink = getRelatedCenterTermLink(
    relatedCenterTermData
  )

  const options = {
    transform: function transform(node, index) {
      if (node.type === 'script') {
        const src = node?.attribs?.src
        if (src && !isAllowedScriptSource(src)) {
          console.warn(`Blocked unauthorized script source: ${src}`)
          return null
        }

        const content = node?.children?.[0]?.data
        if (content && !isAllowedScriptContent(content)) {
          console.warn('Blocked unauthorized script content')
          return null
        }
        if (!embeddedScriptTagSrc) {
          setEmbeddedScriptTagSrc(src)
        }
        return null
      }
    }
  }

  const parseHtmlContent = (content) => {
    return ReactHtmlParser(
      content
        .replace(/luxuryrehab.in/gi, 'luxuryrehabs.com')
        .replace(/http:/gi, 'https:'),
      options
    )
  }

  return (
    <div className="w-full">
      <BlogHero
        blogTitle={blogTitle}
        isCircleOfChairs={isCircleOfChairs}
        isVoices={isVoices}
        isNews={isNews}
        isPodcast={isPodcast}
        authorData={authorData}
        reviewerData={reviewerData}
        publishDate={publishDate}
        reviewedDate={reviewedDate}
        blogSlug={blogSlug}
        heroOptions={heroOptions}
      />
      <div className="mx-auto -mt-4 flex flex-col items-center rounded-t-3xl bg-white p-4 md:mt-0 md:max-w-screen-xl md:rounded-none md:px-8 md:py-20">
        <div className="flex w-full flex-col gap-6 md:max-w-[900px] md:gap-10">
          {isNonEmptyArray(keyPoints) && (
            <>
              <div className="md:px-12">
                <div className="flex flex-col gap-3">
                  <div className="text-xl font-semibold text-gray-1100">
                    Key Points
                  </div>
                  <ul>
                    {keyPoints.map((items) => {
                      return (
                        <li
                          className="ml-7 list-disc text-lg font-medium text-gray-900"
                          key={items?.keyPoints}
                        >
                          {items?.keyPoints}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </div>
              <div className="border-b border-gray-300" />
            </>
          )}

          {isNonEmptyString(pdfDownloadLink) &&
            pdfDownloadLink
              .toLowerCase()
              .includes('https://res.cloudinary.com/rehabpath/') && (
              <>
                <div className="md:px-12">
                  <div>
                    <Button
                      className="w-fit"
                      onClick={downloadAsPdf}
                    >
                      Print the Checklist: {blogTitle}
                    </Button>
                  </div>
                </div>
                <div className="border-b border-gray-300" />
              </>
            )}
          {isNonEmptyArray(tableOfContents) && (
            <>
              <div className="md:px-12">
                <div className="flex flex-col items-start gap-4">
                  <h3 className="text-xl font-semibold text-gray-1100">
                    Table of Contents
                  </h3>
                  <ul
                    id="table-of-contents"
                    className="space-y-2"
                  >
                    {tableOfContents.map((items) => {
                      return (
                        <li
                          className="line-clamp-2 text-gray-1100 underline"
                          key={items?.title}
                        >
                          <a href={`#${items?.link}`}>{items?.title}</a>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </div>
              <div className="border-b border-gray-300" />
            </>
          )}

          <div className="md:px-12">
            <div>
              <div className="min-w-full text-base">
                {htmlContentSplits?.slice(0, 3).map((htmlSplit, index) => (
                  <div
                    key={index}
                    className="prose prose-resources mt-10 min-w-full text-base md:prose-resources-lg first:mt-0"
                  >
                    {parseHtmlContent(htmlSplit)}
                  </div>
                ))}
              </div>
              {(isNonEmptyArray(nearbyRelatedCenters) || isLoading) && (
                <NearbyBlogRelatedCentersCarousel
                  firstTag={firstTag}
                  sectionLink={nearbyRelatedCenterLink}
                  sectionHeading={`Explore ${firstTag} Treatment Centers`}
                  centers={nearbyRelatedCenters}
                  isLoading={isLoading}
                />
              )}
              {htmlContentSplits?.slice(3).map((htmlSplit, index) => (
                <div
                  key={index}
                  className="prose prose-resources mt-10 min-w-full text-base md:prose-resources-lg"
                >
                  {parseHtmlContent(htmlSplit)}
                </div>
              ))}
              {isNonEmptyArray(citedSources) && (
                <Accordion allowZeroExpanded>
                  <AccordionItem>
                    <AccordionItemButton className="accordion__button">
                      <div className="mt-6 flex items-center justify-start gap-2 font-medium text-gray-1100">
                        Sources
                        <IconChevronDown />
                      </div>
                    </AccordionItemButton>

                    <AccordionItemPanel>
                      <ol className="text-sm">
                        {citedSources
                          .filter((item) => Boolean(item?.citedSourceText))
                          .map((items) => {
                            return (
                              <li key={items?.citedSourceText}>
                                {' '}
                                <p className="m-0 inline-block text-sm">
                                  {items?.citedSourceText}
                                </p>
                              </li>
                            )
                          })}
                      </ol>
                    </AccordionItemPanel>
                  </AccordionItem>
                </Accordion>
              )}

              <div className="my-4 border-b border-gray-300" />
              <ReturnToBlogHomeLink
                isNews={isNews}
                isVoices={isVoices}
                isCircleOfChairs={isCircleOfChairs}
                isPodcast={isPodcast}
              />
            </div>
            {(!isNews || !isVoices || !isCircleOfChairs) &&
              isNonEmptyArray(slicedArticles) && (
                <div className="mt-6">
                  <ArticlesGrid
                    articles={slicedArticles}
                    heading="Related Articles"
                  />
                </div>
              )}
            <div className="mt-6">
              <BlogCopyBlock />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const handleTableOfContentsAnchorClick = (e) => {
  e.preventDefault()
  scrollToHref({ element: e.currentTarget })
}

export default BlogContainerDesktop
